import React from 'react';
import _ from 'lodash';
import { Field, useForm } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import {
  getFormFieldProps,
  getFormFieldWrapperProps,
  getValidationFunction,
} from '@/formbuilder/formbuilder.utilities';
import type { ValidatingFormComponent } from '@/formbuilder/formBuilder.types';
import { FormFieldWrapper } from '@/formbuilder/FormFieldWrapper';
import type { InputLengthStyleProps, TextAreaProps, TextFieldProps } from '@seeqdev/qomponents';
import { Icon, TextArea, TextField } from '@seeqdev/qomponents';
import { TranslationWithHTML } from '@/core/ContainerWithHTML.atom';

export interface TextFieldIF extends ValidatingFormComponent<string | number> {
  component: 'TextFieldFormComponent';
  id?: string;
  size: 'sm' | 'lg';
  placeholder?: string;
  autoComplete?: string;
  type?: 'number' | 'text' | 'email' | 'password';
  step?: number;
  maxLength?: number;
  minLength?: number;
  max?: number;
  min?: number;
  rows?: number;
  as?: string;
  onBlur?: (value: string) => void;
  onFocus?: (value: string) => void;
  onKeyDown?: (key: string, value: string) => void;
  disabled?: boolean;
  skipMemo?: boolean;
  autoFocus?: boolean;
  displayError?: boolean;
  customErrorText?: string;
  labelIcon?: string;
  labelIconAction?: () => void;
  labelIconTooltip?: string;
  labelClassNames?: string;
  labelIconClassNames?: string;
  translationParams?: { [key: string]: string | number | React.ReactNode };
  onIconClick?: () => void;
}

export const TextFieldFormComponent: React.FunctionComponent<TextFieldIF> = (props) => {
  const {
    name,
    id,
    extendValidation,
    validation,
    placeholder,
    onChange,
    onBlur,
    onKeyDown,
    type = 'text',
    step,
    testId = 'formControl',
    required,
    minLength,
    maxLength,
    min,
    max,
    customErrorText,
    labelIcon,
    labelIconAction,
    labelIconTooltip,
    labelClassNames,
    labelIconClassNames,
    translationParams,
    tooltip,
    tooltipDelay,
    tooltipPlacement,
    onIconClick,
  } = props;

  const { t } = useTranslation();

  const defaultValidation = (value: string | number) => {
    const strValue = `${value ?? ''}`;
    const numericValue = Number(value);

    return (
      (required && _.isEmpty(_.trim(strValue))) ||
      (minLength ? strValue.length < minLength : false) ||
      (maxLength ? strValue.length > maxLength : false) ||
      (min ? isNaN(numericValue) || numericValue < min : false) ||
      (max ? isNaN(numericValue) || numericValue > max : false)
    );
  };

  const appliedValidation = getValidationFunction(defaultValidation, extendValidation, validation);
  const formState = useForm().getState();
  const showError =
    _.has(formState.errors, name) &&
    formState.hasValidationErrors &&
    ((formState.modified && formState.modified[name]) || formState.submitFailed);

  const labelWithHtml = (
    <TranslationWithHTML translationKey={props.label as string} translationParams={translationParams} />
  );

  return (
    <FormFieldWrapper
      {...getFormFieldWrapperProps(props)}
      id={id ?? `${testId}-wrapper`}
      testId={`${testId}-wrap`}
      label={translationParams ? labelWithHtml : props.label}
      labelIcon={labelIcon}
      labelIconAction={labelIconAction}
      labelIconTooltip={labelIconTooltip}
      labelIconClassNames={labelIconClassNames}
      labelClassNames={labelClassNames}
      showError={showError}
      customErrorText={customErrorText}>
      <Field name={name} validate={appliedValidation}>
        {({ input, meta }) => {
          const properProps = _.assign({}, getFormFieldProps(formState, input, meta, props), {
            // match previous behavior by always translating the provided placeholder key
            placeholder: t(placeholder as string),
            value: input.value,
            onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
              input.onChange(e.target.value);
              onChange?.(e.target.value);
            },
            onBlur: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
              if (_.isFunction(onBlur)) {
                onBlur(e.target.value);
              }
            },
            onKeyDown: (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) =>
              onKeyDown?.(e.key, e.currentTarget.value),
            onFocus: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => e.target.select(),
          });

          const tooltipIcon = tooltip ? (
            <Icon
              icon="fa-question-circle"
              large={true}
              tooltip={t(tooltip)}
              tooltipDelay={tooltipDelay}
              tooltipPlacement={tooltipPlacement}
              extraClassNames="pl5 cursorPointer"
              testId={`${testId}_icon`}
              onClick={_.isFunction(onIconClick) ? onIconClick : _.noop}
            />
          ) : null;

          return props.as === 'textarea' && (props.rows || 0) > 1 ? (
            <div>
              <TextAreaMemo
                id={properProps.id}
                name={properProps.name}
                onChange={properProps.onChange}
                onKeyUp={properProps.onKeyUp}
                onKeyDown={properProps.onKeyDown}
                onFocus={properProps.onFocus}
                onBlur={properProps.onBlur}
                placeholder={properProps.placeholder}
                value={properProps.value}
                cols={properProps.cols}
                showError={properProps.showError}
                readonly={properProps.readonly}
                disabled={properProps.disabled}
                extraClassNames={`width-maximum ${properProps.extraClassNames}`}
                testId={testId}
                rows={properProps.rows}
                autoFocus={properProps.autoFocus}
              />
              {tooltipIcon}
            </div>
          ) : (
            <TextFieldMemo
              id={properProps.id}
              name={properProps.name}
              onChange={properProps.onChange}
              onKeyUp={properProps.onKeyUp}
              onKeyDown={properProps.onKeyDown}
              onFocus={properProps.onFocus}
              onBlur={properProps.onBlur}
              placeholder={properProps.placeholder}
              size={props.size}
              value={properProps.value}
              type={type}
              autoComplete={properProps.autoComplete}
              maxLength={properProps.maxLength}
              minLength={properProps.minLength}
              min={properProps.min}
              max={properProps.max}
              ref={properProps.ref}
              inputGroup={properProps.inputGroup}
              showError={properProps.showError}
              readonly={properProps.readonly}
              disabled={properProps.disabled}
              extraClassNames={`width-maximum ${properProps.extraClassNames}`}
              testId={testId}
              step={step}
              autoFocus={properProps.autoFocus}
              required={properProps.showError && properProps.required}
            />
          );
        }}
      </Field>
    </FormFieldWrapper>
  );
};

export const TextFieldMemo = React.memo<TextFieldProps & InputLengthStyleProps & { skipMemo?: boolean }>(
  TextField,
  (prev, next) =>
    !(
      prev.onChange !== next.onChange ||
      !_.isEqual(prev.value, next.value) ||
      prev.readonly !== next.readonly ||
      prev.placeholder !== next.placeholder ||
      prev.extraClassNames !== next.extraClassNames ||
      prev.skipMemo
    ),
);

export const TextAreaMemo = React.memo<TextAreaProps & { skipMemo?: boolean }>(
  TextArea,
  (prev, next) =>
    !(
      prev.onChange !== next.onChange ||
      !_.isEqual(prev.value, next.value) ||
      prev.readonly !== next.readonly ||
      prev.placeholder !== next.placeholder ||
      prev.extraClassNames !== next.extraClassNames ||
      prev.skipMemo
    ),
);
